import { DatabaseFieldType } from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import { type DatasourceCellValue } from '../content-column-read.ts';
import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  getJiraIssueReferences,
  type JiraIssueResourceInput
} from '../jira-issue/jira-issue-field-read-module.ts';
import {
  EMPTY_JIRA_ISSUE_DETAILS_CONTENT,
  type JiraIssueColumnValue,
  type JiraIssueDetailsReadContent
} from './jira-issue-details-read-content.ts';
import { JiraIssueDetailsValueView } from './jira-issue-details-value-view.tsx';

export type JiraIssueColumnResourceInput = JiraIssueResourceInput & {
  readonly column: string;
};
export type JiraIssueDetailsReadPolicy = {
  readonly getJiraIssueColumnSnapshot?: (
    input: JiraIssueColumnResourceInput
  ) => JiraIssueColumnValue | undefined;
  readonly getJiraIssueColumnResource: (
    input: JiraIssueColumnResourceInput
  ) => FieldReadResource<JiraIssueColumnValue>;
  readonly renderDatasourceValue: (value: DatasourceCellValue) => ReactNode;
};
export type JiraIssueDetailsReadRenderer = (
  content: JiraIssueDetailsReadContent
) => ReactNode;
/** Resolves only the configured column while preserving V1 loaded-issue order and omission. */
export function createJiraIssueDetailsFieldReadModule(
  options?: JiraIssueDetailsReadPolicy & {
    readonly render?: JiraIssueDetailsReadRenderer;
  }
) {
  return defineFieldReadModule<JiraIssueDetailsReadContent>({
    fieldType: DatabaseFieldType.JIRA_ISSUE_DETAILS,
    render:
      options?.render ??
      ((content) =>
        createElement(JiraIssueDetailsValueView, {
          content,
          renderValue: options?.renderDatasourceValue ?? (() => null)
        })),
    compile(field) {
      const fieldId = field.settings.fieldId;
      const column = field.settings.jiraField;
      const isPriority = column === 'priority';

      return Object.freeze({
        fieldId: field.id,
        resolve: () => EMPTY_JIRA_ISSUE_DETAILS_CONTENT,
        getResourceRead({ database, address }) {
          if (!options || !fieldId || !column) {
            return undefined;
          }

          const referenced = database.getField(fieldId);

          if (referenced?.type !== DatabaseFieldType.JIRA_ISSUE) {
            return undefined;
          }

          const issues = getJiraIssueReferences(
            database.getValue({ entryId: address.entryId, fieldId }) ??
              database.getDefaultValue(fieldId)
          );

          if (!issues.length) {
            return undefined;
          }

          const resources = new Map<string, FieldReadResource<JiraIssueColumnValue>>();
          const entries = issues.map((issue) => {
            const input = {
              cloudId: referenced.settings.cloudId,
              serverId: referenced.settings.jiraServerId,
              key: issue.issueKey,
              column
            };
            const cached = options.getJiraIssueColumnSnapshot?.(input);

            if (cached?.value) {
              return { key: issue.key, cached, resource: undefined };
            }

            const resource = options.getJiraIssueColumnResource(input);

            resources.set(resource.key, resource);

            return { key: issue.key, cached, resource };
          });
          const resolve = (
            read: <Value>(
              resource: FieldReadResource<Value>
            ) => FieldReadResourceState<Value>
          ): JiraIssueDetailsReadContent => {
            const items = entries.flatMap(({ key, cached, resource }) => {
              const state = resource
                ? read(resource)
                : { status: 'unavailable' as const };
              const value = state.status === 'ready' ? state.value : cached;

              return value ? [Object.freeze({ key, column: value })] : [];
            });

            return items.length
              ? Object.freeze({
                  kind: 'jira-issue-details',
                  isPriority,
                  items: Object.freeze(items)
                })
              : EMPTY_JIRA_ISSUE_DETAILS_CONTENT;
          };
          const initial = resolve(() => ({ status: 'loading' }));

          return Object.freeze({
            resources: Object.freeze([...resources.values()]),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              let completed = false;
              const content = resolve((resource) => {
                const state = read(resource);

                completed ||= state.status !== 'loading';

                return state;
              });

              return completed ? content : initial;
            }
          });
        }
      });
    }
  });
}
